import { createContext, useContext, useEffect, useState } from "react";
import { getContent } from "../lib/api";
import { DEFAULTS } from "../data/contentRegistry";
import {
  applyTheme,
  cachedTheme,
  DEFAULT_PRIMARY,
  DEFAULT_FOOTER,
  DEFAULT_ACCENT,
  PRIMARY_KEY,
  FOOTER_KEY,
  ACCENT_KEY,
} from "../lib/theme";

const ContentContext = createContext({ t: (k, d) => d ?? DEFAULTS[k] ?? "", refresh: () => {} });

export const useContent = () => useContext(ContentContext);

export const ContentProvider = ({ children }) => {
  const [overrides, setOverrides] = useState({});

  const refresh = () =>
    getContent()
      .then((data) => {
        const d = data || {};
        setOverrides(d);
        // Apply the saved brand colors (or fall back to defaults).
        applyTheme({
          primary: d[PRIMARY_KEY] || DEFAULT_PRIMARY,
          footer: d[FOOTER_KEY] || DEFAULT_FOOTER,
          accent: d[ACCENT_KEY] || DEFAULT_ACCENT,
        });
      })
      .catch(() => {});

  useEffect(() => {
    // Apply the locally cached colors immediately to avoid a color flash
    // before the server content is fetched.
    applyTheme(cachedTheme());
    refresh();
    // Re-fetch latest content when the tab regains focus / becomes visible,
    // so admin edits show up without requiring a manual hard refresh.
    const onFocus = () => refresh();
    const onVisible = () => {
      if (document.visibilityState === "visible") refresh();
    };
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, []);

  const t = (key, fallback) => {
    const v = overrides[key];
    if (v !== undefined && v !== null && v !== "") return v;
    return fallback !== undefined ? fallback : DEFAULTS[key] ?? "";
  };

  return (
    <ContentContext.Provider value={{ t, refresh }}>
      {children}
    </ContentContext.Provider>
  );
};
